/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect } from 'react';
import { Card, Container, Grid, Stack } from '@mui/material';
import { useParams } from 'react-router';
import useTask from '../useTask';
import Form from '../../../components/Form';
import PostalAddressEditor from '../PostalAddressEditor';
import Page from '../../../components/Page';
import TaskDetailEditor from '../TaskDetailEditor';
import Task from '../../../models/Task';
import useUpdateTask from '../useUpdateTask';
import TaskInfo from '../TaskInfo';
import TextField from '../../../components/Form/fields/TextField';
import FileByType from '../FilesByType';
import { FileType } from '../../../enums/FileType';
import CommentsSection from '../CommentsSection';
import TaskLoading from '../TaskLoading';
import StatusHandlerButtons from '../../../utils/StatusHandler';
import { useQuery } from 'react-query';
import { UserRole } from '../../../enums/UserRole';
import Customer from '../../../models/Customer';
import SuggestionSection from '../Suggestions';
import useSeenTask from '../useSeenTask';

const LinguistEditor: React.FC = () => {
    const { id: taskId } = useParams();
    const { data: task } = useTask(taskId);
    const { data: customer } = useQuery<Customer>(['customers/', task?.customerId], {
        enabled: !!task?.customerId,
    });
    const { mutate: updateTask } = useUpdateTask(taskId);
    const { mutate: setSeen } = useSeenTask();

    useEffect(() => {
        setSeen({ id: taskId });
    }, [setSeen, taskId]);

    if (!task) {
        return <TaskLoading />;
    }

    return (
        <Page title="WeTranslate">
            <Form<Task> validationSchema={undefined} onSubmit={updateTask} initialValues={task}>
                <Container maxWidth={'lg'}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <Card sx={{ p: 2 }}>
                                    <TaskInfo task={task} isCustomer={false} />
                                </Card>
                                <FileByType fileType={FileType.TRANSLATED} taskId={taskId} />
                                <FileByType fileType={FileType.ORIGINAL} taskId={taskId} />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <Card sx={{ p: 2, bgcolor: 'background.neutral' }}>
                                    <TaskDetailEditor showTranslationOptions />
                                </Card>
                                {task.hardCopyRequired && (
                                    <Card>
                                        <PostalAddressEditor cannotEdit />
                                    </Card>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack gap={2}>
                                <StatusHandlerButtons
                                    userId={task.userId}
                                    task={task!}
                                    role={UserRole.LINGUIST}
                                    size={'small'}
                                    shouldApprovePrice={customer?.mustApprovePrice ?? false}
                                />

                                <CommentsSection />

                                <SuggestionSection />

                                {task.hardCopyRequired && (
                                    <Card sx={{ p: 2 }}>
                                        <TextField name={'trackingNumber'} label={'Tracking number'} />
                                    </Card>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Form>
        </Page>
    );
};

export default LinguistEditor;
